import { A11yModule } from '@angular/cdk/a11y';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  Type,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { assetIdSignalFromRoute } from '../../../../../utils/asset-route.utils';
import { assetWriteSignal } from '../../../../../utils/asset-permissions.utils';
import { PermissionScope } from '../../../../../types/permission-scope.enum';
import { assetSubResourceBreadcrumb } from '../../../../../utils/breadcrumb.utils';
import { ActivatedRoute, Router } from '@angular/router';
import { PlotlyModule } from 'angular-plotly.js';
import * as PlotlyJS from 'plotly.js-dist-min';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { DateRangeParams } from '../../../../../services/measure-data.service';
import { GraphicService } from '../../../../../services/api/graphic.service';
import { ChartLegendManager } from '../../../../../services/chart-legend.manager';
import { DateNavigationService } from '../../../../../services/date-navigation.service';
import { EnvelopeManager } from '../../../../../services/envelope.manager';
import { FullscreenDirective } from '../../../../../directives/fullscreen.directive';
import { GraphDataOrchestratorService } from '../../../../../services/graph-data-orchestrator.service';
import { ViewPreferencesService } from '../../../../../services/view-preferences.service';
import { GraphExportService } from '../../../../../services/graph-export.service';
import { GraphMeasureSyncService } from '../../../../../services/graph-measure-sync.service';
import { GraphStateManager, hasUnsavedChanges } from '../../../../../services/graph-state.manager';
import { ConfirmationHelperService } from '../../../../../services/confirmation-helper.service';
import { GraphicMetadataService } from '../../../../../services/graphic-metadata.service';
import { MeasureDataService } from '../../../../../services/measure-data.service';
import { MeasureStateManager } from '../../../../../services/measure-state.manager';
import { ModalHelperService } from '../../../../../services/modal-helper.service';
import { DEFAULT_STYLING } from '../../../../../services/default-styling';
import { PolynomialService } from '../../../../../services/polynomial.service';
import { ToastService } from '../../../../../services/toast.service';
import { UnitConversionOrchestratorService } from '../../../../../services/unit-conversion-orchestrator.service';
import { CampaignStore } from '../../../../../stores/campaign.store';
import { GraphStore } from '../../../../../stores/graph.store';
import { CampaignMeasure } from '../../../../../types/campaign.interface';
import { GraphicMeasure, GraphMeasure } from '../../../../../types/graph-data.interface';
import { CurveStyleResult, Envelope, MeasureSelectionResult } from '../../../../../types/graph-dialog.interface';
import { EvTempConfig, Graph, GraphTypeId, YearType } from '../../../../../types/graph.interface';
import { CustomScaleConfig, PlotlyLineDash } from '../../../../../types/graphic-metadata.interface';
import { MeasureConfig, MeasureConfigResult } from '../../../../../types/measure-config.interface';
import { MenuItemWithIcon } from '../../../../../types/menu.interface';
import { MeasureType } from '../../../../../types/table.interface';
import { RouteUtils } from '../../../../../utils/route.utils';
import { isEndBeforeStart } from '../../../../../utils/date-comparison.utils';
import { endOfDay, parseToDate, startOfDay } from '../../../../../utils/date-range-filter.utils';
import { serializeTimestampFilterForUrl } from '../../../../../utils/timestamp-filter-url.utils';
import { buildEntityMenuItems } from '../../../../../utils/entity-menu.utils';
import {
  PlotlyConfig,
  PlotlyDataPoint,
  PlotlyLayout,
  PointClickEvent,
  SelectedPointInfo,
} from '../../../../../types/plotly.interface';
import { formatPointDateTime, getYear } from '../../../../../utils/date.utils';
import { formatNumericValue } from '../../../../../utils/grid/numeric-value-formatter';
import { ChartLegendComponent } from './components/chart-legend.component';
import { EnvelopeManagerComponent } from './components/envelope-manager.component';
import { MeasureSelectorEvTempComponent } from './components/measure-selector-ev-temp.component';
import { MeasureSelectorMultiYearComponent } from './components/measure-selector-multi-year.component';
import { MeasureSelectorXYComponent } from './components/measure-selector-xy.component';
import { CreateGraphDialogComponent } from './create-graph-dialog.component';
import { CurveStyleDialogComponent } from './dialogs/curve-style-dialog.component';
import type { LineType } from './dialogs/curve-style.types';
import {
  DEFAULT_MEASURE_MARKER,
  DEFAULT_POLYNOMIAL_MARKER,
  DEFAULT_POLYNOMIAL_THICKNESS,
  PLOTLY_DASH_TO_LINE_TYPE,
  PLOTLY_LINE_TYPE_MAP,
} from './dialogs/curve-style.constants';
import { EnvelopeCurvesDialogComponent } from './dialogs/envelope-curves-dialog.component';
import { ExportGraphDialogComponent, ExportGraphResult } from './dialogs/export-graph-dialog.component';
import { MeasureConfigDialogComponent } from './dialogs/measure-config-dialog.component';
import { SelectMeasuresDialogComponent } from '../../../../ui/dialogs/select-measures-dialog/select-measures-dialog.component';
import { GraphChartComponent } from './graph-chart.component';
import { GraphHeaderComponent } from './graph-header.component';
import {
  assignTracesToAxes,
  buildCustomScaleAxisMapping,
  EvolutionTemporelleStrategy,
  GraphTransformContext,
  GraphTransformStrategy,
  MultiYearStrategy,
  xyCurveLabel,
  XYScatterStrategy,
} from './graph-transform.strategies';
import { GRAPH_DISPLAY_CONFIG, GRAPH_POINT_BUDGET, GRAPH_TYPE_LABEL, supportsPointBudget } from './graph-view.constants';

PlotlyModule.plotlyjs = PlotlyJS;

const safeNumberConversion = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const numVal = Number(value);
  return Number.isFinite(numVal) ? numVal : null;
};

const sortByTimestamp = (values: CampaignMeasure[]): CampaignMeasure[] =>
  [...values].sort(
    (a, b) => (a.timestamp ? new Date(a.timestamp).getTime() : 0) - (b.timestamp ? new Date(b.timestamp).getTime() : 0),
  );

@Component({
  selector: 'ces-graph-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './graph-view.component.html',
  imports: [
    GraphHeaderComponent,
    GraphChartComponent,
    ChartLegendComponent,
    MeasureSelectorEvTempComponent,
    MeasureSelectorMultiYearComponent,
    MeasureSelectorXYComponent,
    EnvelopeManagerComponent,
    A11yModule,
    TranslocoModule,
    FullscreenDirective,
  ],
  providers: [
    DateNavigationService,
    MeasureStateManager,
    ChartLegendManager,
    EnvelopeManager,
    GraphMeasureSyncService,
    GraphDataOrchestratorService,
    GraphStateManager,
  ],
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class GraphViewComponent implements OnDestroy {
  protected readonly MeasureType = MeasureType;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly graphStore = inject(GraphStore);
  private readonly graphApiService = inject(GraphicService);
  private readonly measureDataService = inject(MeasureDataService);
  private readonly campaignStore = inject(CampaignStore);
  private readonly dialogService = inject(DialogService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly metadataService = inject(GraphicMetadataService);
  private readonly polynomialService = inject(PolynomialService);
  private readonly graphDataOrchestrator = inject(GraphDataOrchestratorService);
  private readonly unitConversionOrchestrator = inject(UnitConversionOrchestratorService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly viewPrefs = inject(ViewPreferencesService);
  private readonly exportService = inject(GraphExportService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());
  private readonly activeLang = toSignal(this.translate.langChanges$, { initialValue: this.translate.getActiveLang() });

  readonly dateNav = inject(DateNavigationService);
  readonly measureMgr = inject(MeasureStateManager);
  readonly legendMgr = inject(ChartLegendManager);
  readonly envelopeMgr = inject(EnvelopeManager);
  private readonly measureSync = inject(GraphMeasureSyncService);
  readonly stateMgr = inject(GraphStateManager);

  readonly graphHeight = GRAPH_DISPLAY_CONFIG.HEIGHT;
  private readonly LIVE_REGION_ANNOUNCEMENT_DURATION = 1000;
  private cachedLegendItems: PlotlyDataPoint[] = [];

  private readonly translateKey = (key: string): string => this.translate.translate(key);
  private readonly getActiveLang = (): string => this.activeLang();

  private readonly transformStrategies: Map<number, GraphTransformStrategy> = new Map<number, GraphTransformStrategy>([
    [1, new EvolutionTemporelleStrategy(sortByTimestamp, safeNumberConversion, this.translateKey, this.getActiveLang)],
    [2, new MultiYearStrategy(sortByTimestamp, safeNumberConversion, this.translateKey, this.getActiveLang)],
    [3, new XYScatterStrategy(safeNumberConversion, this.translateKey, this.getActiveLang)],
  ]);

  readonly currentGraphId = toSignal(this.route.paramMap.pipe(map(params => +(params.get('graphId') ?? '0'))), {
    initialValue: 0,
  });

  readonly currentAssetId = assetIdSignalFromRoute(this.route);
  private readonly canWriteAsset = assetWriteSignal(PermissionScope.Graphics, this.currentAssetId);

  readonly currentGraph = computed(() => {
    const graphId = this.currentGraphId();
    return this.graphStore.byId()(graphId) ?? null;
  });

  readonly graphName = computed(() => this.currentGraph()?.name ?? '');

  readonly graphDescription = computed((): string | null => this.currentGraph()?.description ?? null);

  readonly graphTypeLabel = computed(() => {
    const loaded = this.translations();
    const graph = this.currentGraph();
    if (!graph || !loaded) return '';
    const key = GRAPH_TYPE_LABEL[graph.type];
    return key ? this.translate.translate(key) : this.translate.translate('common.labels.unknownType');
  });

  readonly breadcrumbTrail = computed((): MenuItem[] => {
    this.translations();
    return assetSubResourceBreadcrumb(this.translate, {
      parentLabelKey: 'graphs.breadcrumb',
      parentRouterLink: ['/assets', this.currentAssetId(), 'analysis', 'graphs'],
      currentLabel: this.currentGraph()?.name ?? null,
    });
  });

  readonly menuItems = computed((): MenuItemWithIcon[] => {
    this.translations();
    return buildEntityMenuItems(
      this.translate,
      'graphs.actions',
      {
        onExport: () => this.onExportChart(),
        onEdit: () => this.onEditChart(),
        onDuplicate: () => this.onDuplicateChart(),
        onDelete: () => this.onDeleteChart(),
      },
      { canWrite: this.canWriteAsset() },
    );
  });

  readonly yearType = signal<YearType>('civile');
  readonly isYearTypeLocked = signal(false);
  readonly availableYears = signal(['A']);
  readonly liveRegionMessage = signal<string>('');
  readonly autoRefresh = signal(false);
  readonly graphMeasures = signal<GraphMeasure[]>([]);
  readonly graphicMeasures = signal<GraphicMeasure[]>([]);
  readonly measureValues = signal<Map<number, CampaignMeasure[]>>(new Map());
  readonly sortedMeasureValues = signal<Map<number, CampaignMeasure[]>>(new Map());
  readonly measureConfigs = signal<Map<number, MeasureConfig>>(new Map());
  readonly customScales = signal<Map<number, CustomScaleConfig>>(new Map());
  readonly polynomialStyles = signal<Map<string, { dash: string; width: number; color: string }>>(new Map());
  readonly plotlyRevision = signal(0);
  private readonly previousGraphId = signal<number | null>(null);

  readonly isDirty = computed(() =>
    hasUnsavedChanges(
      {
        graphMeasureIds: this.graphMeasures().map(m => m.id),
        xyAxisIds: this.measureMgr.selectedXYIds(),
        measureConfigs: this.measureConfigs(),
        customScales: this.customScales(),
        traceStyles: this.legendMgr.traceStyles(),
        traceOrder: this.legendMgr.traceOrder(),
        traceVisibility: this.legendMgr.traceVisibility(),
        envelopes: this.envelopeMgr.envelopes(),
        multiYearYears: this.availableYears(),
        yearType: this.yearType(),
      },
      this.stateMgr.savedState(),
    ),
  );

  private readonly convertedTracesCache = signal<PlotlyDataPoint[] | null>(null);
  private readonly conversionInProgress = signal(false);

  // Hash des unités configurées, comparé à sa valeur au dernier chargement : un écart
  // signale un changement d'unité en attente d'application par « Actualiser » (ou l'auto).
  private readonly unitConfigsHash = computed(() =>
    Array.from(this.measureConfigs().entries())
      .sort(([a], [b]) => a - b)
      .map(([measureId, config]) => `${measureId}:${config.unit ?? ''}`)
      .join(';'),
  );
  private readonly appliedUnitConfigsHash = signal('');
  // Unités effectivement appliquées au dernier chargement : le rendu (traces, axes,
  // libellés) s'appuie dessus tant que le changement d'unité n'a pas été actualisé.
  private readonly appliedUnitByMeasure = signal<Map<number, string>>(new Map());

  private readonly renderConfigs = computed(() => {
    const appliedUnits = this.appliedUnitByMeasure();
    const merged = new Map<number, MeasureConfig>();
    for (const [measureId, config] of this.measureConfigs()) {
      const appliedUnit = appliedUnits.get(measureId);
      const { unit: _pendingUnit, ...rest } = config;
      merged.set(measureId, appliedUnit !== undefined ? { ...rest, unit: appliedUnit } : rest);
    }
    return merged;
  });

  private readonly activeMultiYearConfig = signal<{ years: string[]; yearType: string; measureId: number | null }>({
    years: [],
    yearType: '',
    measureId: null,
  });

  private readonly cancelLoad$ = new Subject<void>();

  // Window the user zoomed to, debounced: a drag emits continuously, and only where they settle is
  // worth a round trip.
  private readonly zoomWindow$ = new Subject<readonly [Date, Date] | null>();

  readonly selectedPoint = signal<SelectedPointInfo | null>(null);
  readonly pointInfoPosition = signal<{ x: number; y: number }>({ x: 0, y: 0 });
  readonly campaignName = signal<string | undefined>(undefined);

  readonly campaignUrl = computed(() => {
    const point = this.selectedPoint();
    if (!point?.campaignId) return null;
    const assetId = this.currentAssetId();
    if (!assetId) return null;
    const base = `/assets/${assetId}/monitoring/campaigns/${point.campaignId}/measures`;
    return typeof point.measureId === 'number' ? `${base}?measureId=${point.measureId}` : base;
  });

  // Deep link into the measure's own follow-up grid, filtered down to the clicked point itself.
  // The filter pins the instant so the row to correct is the only one listed, while the fetch
  // window it derives stays the point's whole day — clearing the filter then reveals its
  // neighbours without a reload. Null for envelope points (string measureId, no measureType):
  // they own no follow-up screen.
  readonly measureFollowUpUrl = computed(() => {
    const point = this.selectedPoint();
    const assetId = this.currentAssetId();
    const segment = RouteUtils.getFollowUpRouteSegment(point?.measureType);
    if (!point || !assetId || !segment || typeof point.measureId !== 'number') return null;

    // customdata always carries the raw timestamp; xValue is the plotted X, a bare number on an
    // XY graph, so it would date the link to the epoch.
    const base = `/assets/${assetId}/monitoring/${segment}/${point.measureId}`;
    const at = parseToDate(point.timestamp);
    const filters = at ? serializeTimestampFilterForUrl([at, at]) : null;
    return filters ? `${base}?filters=${filters}` : base;
  });

  private readonly pointInfoPanel = viewChild<ElementRef<HTMLDivElement>>('pointInfoPanel');
  private readonly document = inject(DOCUMENT);

  onDocumentClick(event: MouseEvent): void {
    const panel = this.pointInfoPanel()?.nativeElement;
    if (this.selectedPoint() && panel && !panel.contains(event.target as Node)) {
      this.closePointInfo();
    }
  }

  private focusPointInfoPanel(): void {
    setTimeout(() => this.pointInfoPanel()?.nativeElement?.focus());
  }

  private resetActiveDateRange(): void {
    this.dateNav.clearAppliedDates();
  }

  private getEmptyStateReason(graphType: number, context: GraphTransformContext): 'no-measures' | 'no-dates' | 'needs-refresh' | null {
    if (graphType === 2) {
      const config = this.multiYearConfig();
      if (!config.measure || config.selectedYears.length === 0) return 'no-measures';
      const hasValues = context.values.size > 0 && Array.from(context.values.values()).some(v => v.length > 0);
      return hasValues ? null : 'needs-refresh';
    }

    if (graphType === 3) {
      if (!this.measureMgr.selectedMeasureX() || !this.measureMgr.selectedMeasureY()) return 'no-measures';
      return this.getDateBasedEmptyReason();
    }

    if (context.measures.length === 0) return 'no-measures';
    return this.getDateBasedEmptyReason();
  }

  private getDateBasedEmptyReason(): 'no-dates' | 'needs-refresh' | null {
    if (!this.dateNav.startDate() || !this.dateNav.endDate()) return 'no-dates';
    return this.dateNav.hasAppliedDates() ? null : 'needs-refresh';
  }

  private buildMeasureState() {
    return {
      graphMeasures: this.graphMeasures,
      graphicMeasures: this.graphicMeasures,
      measureValues: this.measureValues,
      sortedMeasureValues: this.sortedMeasureValues,
      measureConfigs: this.measureConfigs,
      customScales: this.customScales,
      availableDateRange: this.availableDateRange,
      isLoadingMeasures: this.isLoadingMeasures,
    };
  }

  private saveCurrentStateAsBaseline(): void {
    this.stateMgr.saveCurrentState(
      this.graphMeasures(),
      this.measureConfigs(),
      this.customScales(),
      this.legendMgr.traceStyles(),
      this.legendMgr.traceOrder(),
      this.legendMgr.traceVisibility(),
      this.envelopeMgr.envelopes(),
      this.availableYears(),
      this.yearType(),
      this.measureMgr.selectedXYIds(),
    );
  }

  private cancelCurrentLoad(): void {
    this.cancelLoad$.next();
  }

  // Undefined for the types that must load whole, which the fetch reads as "send every point".
  private getCurrentPointBudget(): number | undefined {
    const graph = this.currentGraph();
    return graph && supportsPointBudget(graph.type) ? GRAPH_POINT_BUDGET : undefined;
  }

  /**
   * Zooming asks the server for that window again. The budget is per request, so a narrower window
   * spends it on fewer years and the curve gains back the detail the full view had to drop —
   * zoom far enough in and every stored point is on screen.
   *
   * Only the time-evolution graph qualifies: multi-year already loads one window per year and would
   * have to decide which one a zoom belongs to, and an X-Y scatter has no time axis to narrow.
   */
  onXRangeChange(range: readonly [Date, Date] | null): void {
    if (this.currentGraph()?.type !== 1) return;
    this.zoomWindow$.next(range);
  }

  private getCurrentDateRange(): DateRangeParams | DateRangeParams[] {
    if (this.currentGraph()?.type === 2) {
      return this.availableYears().map(yearN => {
        return this.getMultiYearRange(yearN);
      });
    }
    // This path loads straight off the pickers (a graph opens before any refresh applies a window),
    // so it widens the end bound itself, the way the applied window does. Without it the last day
    // asked for returns no points and the curve stops a day short.
    const endDate = this.dateNav.endDate();
    return {
      startDate: this.dateNav.startDate(),
      endDate: endDate ? endOfDay(endDate) : null,
    };
  }

  // A zoom takes over the window while it lasts: it drives the X axis bounds the layout pins, and
  // the client-side date filter, so both agree with the points the zoom fetched. The pickers are
  // left alone and take over again the moment the user zooms back out.
  private readonly zoomedWindow = signal<readonly [Date, Date] | null>(null);

  readonly evTempConfig = computed<EvTempConfig>(() => {
    const zoom = this.zoomedWindow();
    return {
      startDate: zoom ? zoom[0] : this.dateNav.appliedStartDate(),
      endDate: zoom ? zoom[1] : this.dateNav.appliedEndDate(),
    };
  });

  readonly multiYearConfig = computed(() => {
    const measure = this.measureMgr.selectedMeasureMultiYear();
    const yearType = this.yearType();

    return { measure, selectedYears: this.availableYears(), yearType };
  });

  readonly xyConfig = computed(() => ({
    startDate: this.dateNav.appliedStartDate(),
    endDate: this.dateNav.appliedEndDate(),
    measureX: this.measureMgr.selectedMeasureX(),
    measureY: this.measureMgr.selectedMeasureY(),
  }));

  readonly availableDateRange = signal<{ minDate: Date | null; maxDate: Date | null }>({
    minDate: null,
    maxDate: null,
  });

  readonly isLoadingMeasures = signal(false);

  // The pickers feed the load and the auto-refresh hash directly, so an inverted window typed
  // there must hold every refresh, not only the button.
  readonly areDatesValid = computed(() => {
    const start = this.dateNav.startDate();
    const end = this.dateNav.endDate();
    return start !== null && end !== null && !isEndBeforeStart(start, end);
  });

  readonly canRefreshConditions = computed(() => {
    const graph = this.currentGraph();
    if (!graph || this.isLoadingMeasures()) return false;

    switch (graph.type) {
      case 1: // Ev. Temp.
        return this.areDatesValid() && this.graphMeasures().length > 0;
      case 2: // Multi-year
        return this.measureMgr.selectedMeasureMultiYear() !== null && this.availableYears().length > 0;
      case 3: // X-Y
        return this.areDatesValid() && this.measureMgr.selectedMeasureX() !== null && this.measureMgr.selectedMeasureY() !== null;
      default:
        return false;
    }
  });

  readonly canRefresh = computed(() => {
    if (!this.canRefreshConditions()) return false;

    const graph = this.currentGraph();
    if (!graph) return false;

    if (graph.type === 1 || graph.type === 3) {
      const datesChanged = this.dateNav.hasPendingChanges();
      const unitsChanged = this.unitConfigsHash() !== this.appliedUnitConfigsHash();

      if (graph.type === 3) {
        const saved = this.stateMgr.savedState();
        const envelopesChanged = JSON.stringify(this.envelopeMgr.envelopes()) !== JSON.stringify(saved.envelopes);
        return datesChanged || envelopesChanged || unitsChanged;
      }

      return datesChanged || unitsChanged;
    }

    if (graph.type === 2) {
      const currentYears = this.availableYears();
      const currentYearType = this.yearType();
      const currentMeasureId = this.measureMgr.selectedMeasureMultiYear()?.id ?? null;
      const active = this.activeMultiYearConfig();
      const yearsChanged = currentYears.length !== active.years.length || currentYears.some((y, i) => y !== active.years[i]);
      const yearTypeChanged = currentYearType !== active.yearType;
      const measureChanged = currentMeasureId !== active.measureId;
      return yearsChanged || yearTypeChanged || measureChanged;
    }

    return true;
  });

  private readonly conversionTrigger$ = new Subject<{
    measures: GraphMeasure[];
    values: Map<number, CampaignMeasure[]>;
    configs: Map<number, MeasureConfig>;
  }>();

  constructor() {
    this.zoomWindow$
      .pipe(
        debounceTime(300),
        // Redrawing with the fetched points re-emits the same window; without this the reload would
        // trigger itself forever.
        distinctUntilChanged((a, b) => a?.[0].getTime() === b?.[0].getTime() && a?.[1].getTime() === b?.[1].getTime()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(range => {
        const graph = this.currentGraph();
        if (!graph) return;
        this.zoomedWindow.set(range);
        // Zooming back out restores the window the pickers ask for.
        const window = range ? { startDate: range[0], endDate: range[1] } : this.getCurrentDateRange();
        this.loadPreconfiguredMeasures(graph.id, graph.assetId, window, true);
      });

    this.conversionTrigger$
      .pipe(
        debounceTime(300),
        switchMap(({ measures, values, configs }) =>
          this.unitConversionOrchestrator.applyConversions$({ measures, values, configs }).pipe(
            tap(result => {
              if (result.hasConversions && result.convertedValues) {
                this.applyConvertedTracesForEvolutionTemporelle(measures, result.convertedValues, configs);
                if (this.currentGraph()?.type === 1) {
                  // Les traces converties remplacent un rendu autorangé sur les valeurs
                  // brutes : re-calculer les axes une fois le replot effectué.
                  setTimeout(() => void this.onResetAxes());
                }
              } else {
                this.convertedTracesCache.set(null);
              }
              this.conversionInProgress.set(false);
            }),
            catchError(() => {
              this.convertedTracesCache.set(null);
              this.conversionInProgress.set(false);
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.autoRefreshTrigger$.subscribe();

    effect(() => {
      const graphId = this.currentGraphId();
      const graph = this.graphStore.byId()(graphId);

      if (!graph && graphId) {
        this.graphStore.loadById(graphId).catch((error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 404) {
            void this.router.navigate(['/404']);
          }
        });
      }
    });

    effect(() => {
      const graph = this.currentGraph();
      const prevId = this.previousGraphId();

      if (graph && graph.id !== prevId) {
        this.cancelCurrentLoad();
        this.graphMeasures.set([]);
        this.measureValues.set(new Map());
        this.measureConfigs.set(new Map());
        this.customScales.set(new Map());
        this.graphicMeasures.set([]);
        this.convertedTracesCache.set(null);
        this.resetActiveDateRange();
        this.measureMgr.reset();
        this.legendMgr.initializeTraces([], false);
        this.envelopeMgr.clear();
        this.cachedLegendItems = [];
        this.stateMgr.reset();
        this.previousGraphId.set(graph.id);

        const config = this.metadataService.parseGraphMetadata(graph.metadata);
        if (graph.type === 2 && config) {
          this.availableYears.set(this.measureMgr.normalizeStoredYears(config.selectedYears ?? []));
          this.yearType.set(config.yearType ?? 'civile');
          this.isYearTypeLocked.set(config.yearType != null);
        } else if (graph.type === 2) {
          this.isYearTypeLocked.set(false);
        }

        if (graph.type === 1 || graph.type === 3) {
          const { startDate, endDate } = this.viewPrefs.getDateRange();
          if (startDate && endDate) {
            this.dateNav.setDates(startDate, endDate);
          }
        }

        this.autoRefresh.set(this.viewPrefs.getAutoRefresh());
        this.legendMgr.panelCollapsed.set(this.viewPrefs.getLegendCollapsed());

        if (graph.assetId !== undefined) {
          this.loadMeasuresMetadataOnly(graph.id, graph.assetId);
        }
      }
    });

    effect(() => {
      const measures = this.graphMeasures();
      const values = this.measureValues();
      // Configs lues sans tracking : un changement d'unité ne reconvertit pas le graphe
      // affiché — il reste en attente jusqu'au prochain chargement (« Actualiser » ou auto).
      const configs = untracked(() => this.measureConfigs());

      this.conversionInProgress.set(true);
      this.conversionTrigger$.next({ measures, values, configs });
      this.appliedUnitConfigsHash.set(untracked(() => this.unitConfigsHash()));
      const appliedUnits = new Map<number, string>();
      for (const [measureId, config] of configs) {
        if (config.unit) appliedUnits.set(measureId, config.unit);
      }
      this.appliedUnitByMeasure.set(appliedUnits);
    });
  }

  private readonly autoRefreshHash = computed(() => {
    if (!this.autoRefresh()) return '';

    const graph = this.currentGraph();
    if (!graph) return '';

    const configs = this.measureConfigs();
    const configsHash = Array.from(configs.entries())
      .sort(([a], [b]) => a - b)
      .map(
        ([k, v]) =>
          `${k}:${v.unit ?? ''},${v.polynomInferieur},${v.polynomSuperieur},${v.customScale},${v.scaleMin ?? ''},${v.scaleMax ?? ''}`,
      )
      .join(';');

    const parts: (string | number)[] = [
      this.graphMeasures()
        .map(m => m.id)
        .join(','),
      configsHash,
    ];

    if (graph.type === 1 || graph.type === 3) {
      parts.push(this.dateNav.startDate()?.getTime() ?? 0);
      parts.push(this.dateNav.endDate()?.getTime() ?? 0);
    }

    if (graph.type === 2) {
      parts.push(this.availableYears().join(','));
      parts.push(this.yearType());
    }

    if (graph.type === 3) {
      const envelopesHash = this.envelopeMgr
        .envelopes()
        .map(e => {
          const points = e.points.map(p => `${p.x},${p.y}`).join('|');
          return `${e.id}:${e.name}:${points}:${e.metadata?.visual?.color ?? ''}`;
        })
        .join(';');
      parts.push(envelopesHash);
    }

    return parts.join('|');
  });

  private readonly autoRefreshTrigger$ = toObservable(this.autoRefreshHash).pipe(
    filter(hash => {
      if (!hash) return false;
      return this.autoRefresh() && this.canRefresh() && !this.isLoadingMeasures();
    }),
    distinctUntilChanged(),
    debounceTime(200),
    tap(() => this.onRefresh()),
    takeUntilDestroyed(this.destroyRef),
  );

  // Local whole-day bounds: a `${year}-01-01` string parses as UTC midnight, which is 01:00 local
  // and would leave the year's first hour outside its own range once the bounds travel as instants.
  private getMultiYearRange(yearN: string) {
    const year = getYear(yearN, this.yearType());
    const civilYear = this.yearType() === 'civile';
    const startDate = civilYear ? new Date(year, 0, 1) : new Date(year, 9, 1);
    const endDate = civilYear ? new Date(year, 11, 31) : new Date(year + 1, 8, 30);
    return { startDate, endDate: endOfDay(endDate) };
  }

  private isPolynomialTrace(measureId: number | string): measureId is string {
    return typeof measureId === 'string' && measureId.includes('_');
  }

  private isValidId(id: number | null | undefined): id is number {
    return id !== null && id !== undefined && Number.isInteger(id) && id > 0 && id < 2147483647;
  }

  private setMeasuresForGraphType(graphType: GraphTypeId, measures: GraphMeasure[]): void {
    if (graphType === 1) {
      this.measureMgr.setMeasuresEvTemp(measures, this.measureConfigs());
    } else if (graphType === 2 && measures[0]) {
      this.measureMgr.setMeasure('multiYear', measures[0], this.measureConfigs());
    } else if (graphType === 3 && measures.length >= 2) {
      const orderedIds = [...this.graphicMeasures()].sort((a, b) => a.sortNr - b.sortNr).map(gm => gm.measureId);
      this.measureMgr.seedXYAxes(orderedIds, measures, this.measureConfigs());
    }
  }

  private buildFallbackLayout(): Partial<PlotlyLayout> {
    return {
      font: { size: 12, family: 'Sora, sans-serif', color: '#444' },
      xaxis: { title: { text: '' }, showgrid: true },
      yaxis: { title: { text: '' }, showgrid: true },
      showlegend: true,
      hovermode: 'closest',
      autosize: true,
      margin: { l: 60, r: 40, t: 40, b: 60 },
    };
  }

  readonly plotlyData = computed<PlotlyDataPoint[]>(() => {
    this.legendMgr.traceOrder();
    const graph = this.currentGraph();
    if (!graph) return [];

    if (this.isLoadingMeasures()) return [];

    if ((graph.type === 1 || graph.type === 3) && !this.dateNav.hasAppliedDates()) {
      return [];
    }

    const strategy = this.transformStrategies.get(graph.type);
    if (!strategy) return [];

    const context: GraphTransformContext = {
      measures: this.graphMeasures(),
      values: this.measureValues(),
      configs: this.renderConfigs(),
      customScales: this.customScales(),
      traceOrder: untracked(() => this.legendMgr.traceOrder()),
      traceStyles: untracked(() => this.legendMgr.traceStyles()),
      envelopes: this.envelopeMgr.envelopes(),
    };

    if (this.getEmptyStateReason(graph.type, context)) return [];

    const config = graph.type === 1 ? this.evTempConfig() : graph.type === 2 ? this.multiYearConfig() : this.xyConfig();

    let traces = strategy.transformData(context, config);

    if (graph.type === 1 && context.measures.length > 0) {
      const hasConversions = Array.from(context.configs.values()).some(cfg => cfg.unit && cfg.unit.trim() !== '');
      const convertedCache = this.convertedTracesCache();

      const measureTraces = hasConversions && convertedCache ? convertedCache : this.applyTraceStyles(traces, context.traceStyles);

      const polynomialTraces = this.polynomialService.generateTraces(
        context.measures,
        this.sortedMeasureValues(),
        context.configs,
        measureTraces,
        context.traceStyles,
        config as EvTempConfig,
      );

      const allTraces = [...measureTraces, ...polynomialTraces];
      const tracesWithAxes = assignTracesToAxes(allTraces, context.measures, context.customScales, context.configs);

      return untracked(() => this.legendMgr.applyStylesToData(tracesWithAxes));
    }

    const styledTraces = untracked(() => this.legendMgr.applyStylesToData(traces));
    return graph.type === 2 ? this.applyYearLabels(styledTraces) : styledTraces;
  });

  private applyYearLabels(traces: PlotlyDataPoint[]): PlotlyDataPoint[] {
    const yt = this.yearType();
    const measureName = this.measureMgr.selectedMeasureMultiYear()?.name ?? '';
    return traces.map(t => {
      if (!t.traceMetadata?.yearN) return t;
      const year = getYear(String(t.traceMetadata.yearN), yt);
      const label = yt === 'hydraulique' ? `${year}-${year + 1}` : `${year}`;
      return { ...t, name: `${label} ${measureName}`.trim() };
    });
  }

  readonly plotlyDataForChart = computed<PlotlyDataPoint[]>(() => {
    // Legend order drives the z-order across every axis via Plotly's per-trace
    // `zorder` (higher = foreground), which layers above the axis-subplot stacking.
    // The array is reversed so the legend top ends up last; ascending zorder then
    // puts the legend top in front without touching axis assignment.
    return [...this.plotlyData()].reverse().map((trace, index) => ({ ...trace, zorder: index }));
  });

  // Interact whenever the legend lists measures; reorder/visibility act on the
  // signals below, not the plotted canvas, and persist via Save.
  readonly legendInteractive = computed<boolean>(() => this.legendItems().length > 0);

  readonly legendItems = computed<PlotlyDataPoint[]>(() => {
    // Track order/visibility so reorder + toggles reflect before the chart is
    // plotted; the fallback path reads them via untracked applyStylesToData.
    this.legendMgr.traceOrder();
    this.legendMgr.traceVisibility();
    this.legendMgr.traceStyles();
    const plotly = this.plotlyData();
    if (plotly.length > 0) {
      const styledPlotly = this.legendMgr.applyStylesToData(plotly);
      if (this.currentGraph()?.type === 2) {
        styledPlotly.forEach((trace, i) => {
          if (plotly[i]?.name) trace.name = plotly[i].name;
        });
      }
      this.cachedLegendItems = styledPlotly;
      return styledPlotly;
    }

    if (this.isLoadingMeasures() && this.cachedLegendItems.length > 0) {
      return this.cachedLegendItems;
    }

    const graph = this.currentGraph();
    if (!graph) return [];

    let baseTraces: PlotlyDataPoint[];

    if (graph.type === 3) {
      const measureX = this.measureMgr.selectedMeasureX();
      const measureY = this.measureMgr.selectedMeasureY();
      if (!measureX || !measureY) return [];

      const defaultColor = this.paletteColor(0);
      baseTraces = [
        {
          x: [],
          y: [],
          name: xyCurveLabel(measureX, measureY),
          type: 'scatter' as const,
          mode: 'lines+markers' as const,
          line: { color: defaultColor, width: 2 },
          marker: { color: defaultColor, symbol: 'circle' },
          traceMetadata: { measureId: measureX.id },
        },
      ];

      const envelopes = this.envelopeMgr.envelopes();
      envelopes.forEach((env, index) => {
        const envColor = this.paletteColor(index + 1);
        baseTraces.push({
          x: [],
          y: [],
          name: env.name,
          type: 'scatter' as const,
          mode: 'lines' as const,
          line: { color: envColor, width: 2 },
          traceMetadata: { measureId: `env#${env.id}` },
        });
      });
    } else if (graph.type === 2) {
      const config = this.multiYearConfig();
      const measure = config.measure;
      if (!measure || config.selectedYears.length === 0) return [];

      baseTraces = config.selectedYears.map((yearN, index) => {
        const year = getYear(yearN, this.yearType());
        const defaultColor = this.paletteColor(index);
        const yearLabel = this.yearType() === 'hydraulique' ? `${year}-${year + 1}` : `${year}`;
        return {
          x: [],
          y: [],
          name: `${yearLabel} ${measure.name}`,
          type: 'scatter' as const,
          mode: 'lines+markers' as const,
          line: { color: defaultColor, width: 2 },
          marker: { color: defaultColor, symbol: 'circle' },
          traceMetadata: { measureId: measure.id, yearN },
        };
      });
    } else {
      const measures = this.graphMeasures();
      if (measures.length === 0) return [];

      baseTraces = measures.map((measure, index) => {
        const defaultColor = this.paletteColor(index);
        return {
          x: [],
          y: [],
          name: measure.name,
          type: 'scatter' as const,
          mode: 'lines+markers' as const,
          line: { color: defaultColor, width: 2 },
          marker: { color: defaultColor, symbol: 'circle' },
          traceMetadata: { measureId: measure.id },
        };
      });
    }

    return untracked(() => this.legendMgr.applyStylesToData(baseTraces));
  });

  readonly plotlyLayout = computed<Partial<PlotlyLayout>>(() => {
    this.translations();
    const graph = this.currentGraph();
    if (!graph) return {};

    const strategy = this.transformStrategies.get(graph.type);
    if (!strategy) return this.buildFallbackLayout();

    const context: GraphTransformContext = {
      measures: this.graphMeasures(),
      values: this.measureValues(),
      configs: this.renderConfigs(),
      customScales: this.customScales(),
      traceOrder: untracked(() => this.legendMgr.traceOrder()),
      traceStyles: untracked(() => this.legendMgr.traceStyles()),
    };

    const config = graph.type === 1 ? this.evTempConfig() : graph.type === 2 ? this.multiYearConfig() : this.xyConfig();
    const emptyStateReason = this.getEmptyStateReason(graph.type, context);

    return strategy.buildLayout(context, config, emptyStateReason);
  });

  readonly plotlyChartLayout = computed<Partial<PlotlyLayout>>(() => {
    const baseLayout = this.plotlyLayout();
    const showNativeLegend = this.legendMgr.panelCollapsed();
    const datarevision = this.plotlyRevision();
    // Keyed on the graph, so redrawing with the points a zoom just fetched keeps the very range that
    // asked for them. Reset it here and the redraw would zoom back out, ask again, and never settle.
    const uirevision = `graph-${this.currentGraphId()}`;

    if (showNativeLegend) {
      return {
        ...baseLayout,
        showlegend: true,
        legend: {
          orientation: 'h' as const,
          yanchor: 'top' as const,
          y: -0.18,
          xanchor: 'center' as const,
          x: 0.5,
          font: { size: 11, family: 'Sora, sans-serif' },
          itemsizing: 'constant' as const,
          traceorder: 'normal' as const,
        },
        margin: { l: 60, r: 20, t: 20, b: 100 },
        datarevision,
        uirevision,
      };
    }

    return { ...baseLayout, showlegend: false, datarevision, uirevision };
  });

  readonly plotlyConfig: Partial<PlotlyConfig> = {
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
    doubleClick: false,
  };

  private openDialog<T>(component: Type<unknown>, config: DynamicDialogConfig, onSuccess: (result: T) => void): void {
    const ref = this.dialogService.open(component, config);
    ref?.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: T) => {
      if (result) onSuccess(result);
    });
  }

  private loadMeasuresMetadataOnly(graphId: number, assetId: number): void {
    if (!this.isValidId(graphId) || !this.isValidId(assetId)) return;

    this.isLoadingMeasures.set(true);
    const graphType = this.currentGraph()?.type;

    this.graphApiService
      .getGraphicMeasures(graphId)
      .pipe(
        takeUntil(this.cancelLoad$),
        takeUntilDestroyed(this.destroyRef),
        switchMap(preconfiguredMeasures => {
          this.graphicMeasures.set(preconfiguredMeasures);

          if (preconfiguredMeasures.length === 0) {
            return of({ measures: [] as GraphMeasure[], envelopes: null as Envelope[] | null });
          }

          const measureIds = preconfiguredMeasures.map((gm: GraphicMeasure) => gm.measureId);
          const measures$ = this.measureDataService.getMeasuresByIds(assetId, measureIds);
          const envelopes$ = graphType === 3 ? this.graphApiService.getEnvelopes(graphId) : of(null as Envelope[] | null);

          return forkJoin({ measures: measures$, envelopes: envelopes$ });
        }),
        finalize(() => this.isLoadingMeasures.set(false)),
      )
      .subscribe({
        next: result => {
          if (result.envelopes) {
            result.envelopes.forEach(env => this.envelopeMgr.add(env));
          }
          if (result.measures.length === 0) return;

          const selected = result.measures.filter(m => m.selected);
          this.graphMeasures.set(selected);

          if (graphType) {
            const deserialized = this.metadataService.deserializeFromMetadata(
              this.graphicMeasures(),
              graphType,
              result.envelopes ?? undefined,
            );

            if (deserialized.styles.size > 0) {
              this.legendMgr.traceStyles.set(deserialized.styles);
            }
            if (deserialized.order.length > 0) {
              this.legendMgr.traceOrder.set(deserialized.order);
              this.legendMgr.traceVisibility.set(deserialized.visibility);
            } else {
              let traceIds: (number | string)[];
              if (graphType === 3) {
                const sortedGm = [...this.graphicMeasures()].sort((a, b) => a.sortNr - b.sortNr);
                const xMeasureId = sortedGm[0]?.measureId;
                const envelopeIds = (result.envelopes ?? []).map(env => `env#${env.id}`);
                traceIds = xMeasureId ? [xMeasureId, ...envelopeIds] : envelopeIds;
              } else {
                traceIds = selected.map(m => m.id as number | string);
              }
              this.legendMgr.traceOrder.set(traceIds);
              this.legendMgr.traceVisibility.set(new Array<boolean>(traceIds.length).fill(true));
            }

            if (deserialized.measureConfigs.size > 0) {
              this.measureConfigs.set(deserialized.measureConfigs);
            }
            if (deserialized.customScales.size > 0) {
              this.customScales.set(deserialized.customScales);
            }
          }

          if (graphType) {
            this.setMeasuresForGraphType(graphType, selected);
          }

          requestAnimationFrame(() => this.saveCurrentStateAsBaseline());
        },
      });
  }

  private loadPreconfiguredMeasures(
    graphId: number,
    assetId: number,
    dateRange?: DateRangeParams | DateRangeParams[],
    isRefresh = false,
  ): void {
    this.cancelCurrentLoad();
    this.isLoadingMeasures.set(true);

    const state = this.buildMeasureState();

    this.graphDataOrchestrator
      .loadPreconfiguredMeasures$(graphId, assetId, state, dateRange, isRefresh, this.getCurrentPointBudget())
      .pipe(
        takeUntil(this.cancelLoad$),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoadingMeasures.set(false)),
      )
      .subscribe({
        next: result => {
          if (!result || result.measures.length === 0) {
            return;
          }

          const { measures, measureValues } = result;
          const graph = this.currentGraph();
          if (!graph) return;
          this.graphDataOrchestrator.applyMeasuresData(
            measures,
            measureValues,
            state,
            this.legendMgr,
            this.envelopeMgr,
            graph.type,
            isRefresh,
          );

          this.setMeasuresForGraphType(graph.type, measures);

          if (!isRefresh) {
            this.saveCurrentStateAsBaseline();
            return;
          }

          if (graph.type === 1 || graph.type === 3) {
            this.viewPrefs.saveDateRange(this.dateNav.startDate(), this.dateNav.endDate());
            this.dateNav.applyDates();
          }

          this.plotlyRevision.update(r => r + 1);
        },
        error: () => {},
      });
  }

  selectMeasures(): void {
    const assetId = this.currentAssetId();
    const graphId = this.currentGraphId();

    if (!this.isValidId(assetId) || !this.isValidId(graphId)) {
      return;
    }

    const preselectedIds = this.measureMgr.selectedMeasuresEvTemp().map(m => m.id);

    this.isLoadingMeasures.set(true);

    const dialogRef = this.dialogService.open(
      SelectMeasuresDialogComponent,
      this.modalHelper.getFullscreenConfig({
        header: this.translate.translate('common.dialogs.selectMeasures.title'),
        data: {
          assetId,
          preselectedIds,
        },
      }),
    );

    dialogRef.onClose
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        switchMap((result: MeasureSelectionResult | undefined) => {
          if (!result) {
            return of(null);
          }

          this.measureMgr.setMeasuresEvTemp(result.selectedMeasures);

          if (result.selectedMeasures.length === 0) {
            this.graphMeasures.set([]);
            this.measureValues.set(new Map());
            this.legendMgr.initializeTraces([], false);
            return of(null);
          }

          return this.measureDataService.getMeasuresWithValues(
            result.selectedMeasures,
            this.getCurrentDateRange(),
            this.getCurrentPointBudget(),
          );
        }),
        finalize(() => this.isLoadingMeasures.set(false)),
      )
      .subscribe({
        next: result => {
          if (!result) return;
          const graph = this.currentGraph();
          if (!graph) return;
          const { measures, measureValues } = result;
          // Changer la sélection garde les styles et configs non enregistrés
          // des mesures conservées, comme un rechargement.
          this.graphDataOrchestrator.applyMeasuresData(
            measures,
            measureValues,
            this.buildMeasureState(),
            this.legendMgr,
            this.envelopeMgr,
            graph.type,
            true,
          );
          this.measureMgr.setMeasuresEvTemp(measures, this.measureConfigs());
          this.resetActiveDateRange();
        },
        error: () => {},
      });
  }

  addAxisMeasure(axis: 'x' | 'y' | 'multiYear'): void {
    const assetId = this.currentAssetId();
    const graphId = this.currentGraphId();

    if (!this.isValidId(assetId) || !this.isValidId(graphId)) {
      return;
    }

    const getSelected = {
      multiYear: this.measureMgr.selectedMeasureMultiYear,
      x: this.measureMgr.selectedMeasureX,
      y: this.measureMgr.selectedMeasureY,
    };
    const currentlySelectedId = getSelected[axis]()?.id;
    // XY : l'échange d'axes (UAT-014) peut changer X même quand c'est Y qui est
    // remplacée ; la légende compare donc les deux mesures d'avant et d'après.
    const previousXY = this.xyConfig();
    const axisLabel =
      axis === 'multiYear'
        ? this.translate.translate('graphs.axisLabels.measureOrPost')
        : this.translate.translate('graphs.axisLabels.measureOnAxis', { axis: axis.toUpperCase() });

    this.graphApiService
      .getGraphicMeasures(graphId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(preconfiguredMeasures => {
          const preconfiguredIds = preconfiguredMeasures.map((gm: GraphicMeasure) => gm.measureId);
          const allSelectedIds = currentlySelectedId
            ? Array.from(new Set([...preconfiguredIds, currentlySelectedId]))
            : preconfiguredIds;

          const dialogRef = this.dialogService.open(
            SelectMeasuresDialogComponent,
            this.modalHelper.getFullscreenConfig({
              header: this.translate.translate('graphs.dialogs.selectAxis', { axis: axisLabel }),
              data: {
                assetId,
                multiselect: false,
                preselectedIds: allSelectedIds,
              },
            }),
          );

          return (dialogRef.onClose as Observable<MeasureSelectionResult | undefined>).pipe(take(1));
        }),
        switchMap((result: MeasureSelectionResult | undefined) => {
          if (!result) {
            return of(null);
          }

          if (result.selectedMeasures.length === 0 && axis === 'multiYear') {
            this.graphMeasures.set([]);
            this.measureValues.set(new Map());
            this.legendMgr.initializeTraces([], false);
            return of(null);
          }

          if (axis === 'multiYear') {
            const nextMeasure = result.selectedMeasures[0] ?? null;
            this.graphMeasures.set(result.selectedMeasures);
            this.measureValues.set(new Map());
            this.cachedLegendItems = [];
            if (nextMeasure) {
              this.legendMgr.replaceMultiYearMeasure(currentlySelectedId, nextMeasure.id);
            } else {
              this.legendMgr.initializeTraces([], false);
            }
            this.measureMgr.setMeasure(axis, nextMeasure);
            return of(null);
          }

          this.isLoadingMeasures.set(true);
          this.measureMgr.assignXYMeasure(axis, result.selectedMeasures[0] ?? null);
          this.legendMgr.replaceXYMeasures(previousXY, this.xyConfig());

          return this.measureDataService.getMeasuresWithValues(
            result.selectedMeasures,
            this.getCurrentDateRange(),
            this.getCurrentPointBudget(),
          );
        }),
        finalize(() => this.isLoadingMeasures.set(false)),
      )
      .subscribe({
        next: result => {
          // multiYear resolves earlier in the chain (`of(null)`), so a non-null
          // result narrows the axis to x/y for the swap-aware assignment below.
          if (result && axis !== 'multiYear') {
            const { measures, measureValues } = result;
            const axisMeasure = measures[0];
            if (axis === 'x') {
              const mesY = this.measureMgr.selectedMeasureY();
              if (mesY) {
                measures.push(mesY);
              }
            } else if (axis === 'y') {
              const mesX = this.measureMgr.selectedMeasureX();
              if (mesX) {
                measures.unshift(mesX);
              }
            }
            const currentGraph = this.currentGraph();
            if (!currentGraph) return;
            // Remplacer une mesure garde l'état local (styles, ordre, configs)
            // comme un rechargement : les métadonnées persistées ne connaissent
            // ni la nouvelle mesure ni les réglages non enregistrés.
            this.graphDataOrchestrator.applyMeasuresData(
              measures,
              measureValues,
              this.buildMeasureState(),
              this.legendMgr,
              this.envelopeMgr,
              currentGraph.type,
              true,
            );
            this.measureMgr.assignXYMeasure(axis, axisMeasure ?? null, this.measureConfigs());
          }
        },
        error: () => {},
      });
  }

  removeMeasure(axis: 'x' | 'y' | 'multiYear'): void {
    const state = this.measureMgr.getState(axis);
    if (state.measure) {
      const previousXY = this.xyConfig();
      this.measureMgr.removeMeasure(axis);
      if (axis !== 'multiYear') this.legendMgr.replaceXYMeasures(previousXY, this.xyConfig());
    }
  }

  configureMeasure(axis: 'x' | 'y' | 'multiYear'): void {
    const state = this.measureMgr.getState(axis);
    if (!state.measure) return;
    const measure = state.measure;

    this.openMeasureConfigDialog(
      measure,
      state.config ?? null,
      () => this.removeMeasure(axis),
      (config, sharedMeasureIds) => {
        this.measureMgr.updateConfig(axis, config);

        this.applyMeasureConfigUpdate(measure.id, config, sharedMeasureIds);
      },
    );
  }

  configureMeasureEvTemp(measure: GraphMeasure): void {
    const state = this.measureMgr.measureStatesEvTemp().find(s => s.measure?.id === measure.id);

    this.openMeasureConfigDialog(
      measure,
      state?.config ?? null,
      () => this.removeMeasureEvTemp(measure),
      (config, sharedMeasureIds) => {
        this.measureMgr.updateConfigEvTemp(measure.id, config);

        if (sharedMeasureIds?.length) {
          for (const id of sharedMeasureIds) {
            const existingState = this.measureMgr.measureStatesEvTemp().find(s => s.measure?.id === id);
            if (existingState?.config) {
              this.measureMgr.updateConfigEvTemp(id, {
                ...existingState.config,
                ...(config.scaleLabel !== undefined && { scaleLabel: config.scaleLabel }),
              });
            }
          }
        }

        this.applyMeasureConfigUpdate(measure.id, config, sharedMeasureIds);
      },
    );
  }

  private applyMeasureConfigUpdate(measureId: number, config: MeasureConfig, sharedMeasureIds?: number[]): void {
    this.measureConfigs.update(configs => {
      const newConfigs = new Map(configs);
      newConfigs.set(measureId, config);
      if (sharedMeasureIds?.length) {
        this.propagateLabelToConfigs(newConfigs, config, sharedMeasureIds);
      }
      return newConfigs;
    });

    this.customScales.update(scales => {
      const newScales = new Map(scales);
      newScales.delete(measureId);
      if (config.customScale) {
        newScales.set(measureId, {
          enabled: true,
          ...(config.unit !== undefined && { unit: config.unit }),
          ...(config.scaleMin !== undefined && { min: config.scaleMin }),
          ...(config.scaleMax !== undefined && { max: config.scaleMax }),
          ...(config.scaleLabel != null && { label: config.scaleLabel }),
        });
        if (sharedMeasureIds?.length) {
          this.propagateLabelToScales(newScales, config, sharedMeasureIds);
        }
      }
      return newScales;
    });

    this.plotlyRevision.update(r => r + 1);
  }

  private openMeasureConfigDialog(
    measure: GraphMeasure,
    config: MeasureConfig | null,
    onRemove: () => void,
    onSave: (config: MeasureConfig, sharedMeasureIds?: number[]) => void,
  ): void {
    this.openDialog<MeasureConfigResult>(
      MeasureConfigDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translate.translate('graphs.dialogs.editMeasure', { name: measure.name }),
        data: {
          measure,
          config,
          graphType: this.currentGraph()?.type,
          otherMeasures: this.graphMeasures().filter(m => m.id !== measure.id),
          customScales: this.customScales(),
          measureConfigs: this.measureConfigs(),
        },
      }),
      result => {
        if (result.action === 'remove') {
          onRemove();
        } else if (result.action === 'save' && result.config) {
          onSave(result.config, result.sharedMeasureIds);
        }
      },
    );
  }

  private propagateLabelToConfigs(
    configs: Map<number, MeasureConfig>,
    config: MeasureConfig,
    sharedMeasureIds: number[],
  ): Map<number, MeasureConfig> {
    for (const id of sharedMeasureIds) {
      const existing = configs.get(id);
      if (existing) {
        configs.set(id, {
          ...existing,
          ...(config.scaleLabel !== undefined && { scaleLabel: config.scaleLabel }),
        });
      } else {
        configs.set(id, {
          ...(config.unit !== undefined && { unit: config.unit }),
          polynomInferieur: false,
          polynomSuperieur: false,
          customScale: true,
          ...(config.scaleMin !== undefined && { scaleMin: config.scaleMin }),
          ...(config.scaleMax !== undefined && { scaleMax: config.scaleMax }),
          ...(config.scaleLabel !== undefined && { scaleLabel: config.scaleLabel }),
        });
      }
    }
    return configs;
  }

  private propagateLabelToScales(
    scales: Map<number, CustomScaleConfig>,
    config: MeasureConfig,
    sharedMeasureIds: number[],
  ): Map<number, CustomScaleConfig> {
    for (const id of sharedMeasureIds) {
      const existing = scales.get(id);
      if (existing) {
        scales.set(id, {
          ...existing,
          ...(config.scaleLabel != null && { label: config.scaleLabel }),
        });
      } else {
        scales.set(id, {
          enabled: true,
          ...(config.unit !== undefined && { unit: config.unit }),
          ...(config.scaleMin !== undefined && { min: config.scaleMin }),
          ...(config.scaleMax !== undefined && { max: config.scaleMax }),
          ...(config.scaleLabel != null && { label: config.scaleLabel }),
        });
      }
    }
    return scales;
  }

  removeMeasureEvTemp(measure: GraphMeasure): void {
    this.measureMgr.removeMeasureEvTemp(measure);

    const updatedMeasures = this.graphMeasures().filter(m => m.id !== measure.id);
    this.graphMeasures.set(updatedMeasures);
    this.resetActiveDateRange();

    const updatedValues = new Map(this.measureValues());
    updatedValues.delete(measure.id);
    this.measureValues.set(updatedValues);

    this.measureConfigs.update(configs => {
      const newConfigs = new Map(configs);
      newConfigs.delete(measure.id);
      return newConfigs;
    });

    this.convertedTracesCache.set(null);

    this.customScales.update(scales => {
      const newScales = new Map(scales);
      newScales.delete(measure.id);
      return newScales;
    });

    this.plotlyRevision.update(r => r + 1);

    this.measureSync.syncDateRangeFromMeasures(updatedMeasures);

    if (updatedMeasures.length > 0) {
      const { min, max } = this.measureSync.calculateMinMaxForMeasure(Array.from(updatedValues.values()).flat());
      this.availableDateRange.set({ minDate: min, maxDate: max });
    } else {
      this.availableDateRange.set({ minDate: null, maxDate: null });
    }
  }

  addEnvelope(): void {
    this.openDialog<Envelope>(
      EnvelopeCurvesDialogComponent,
      this.modalHelper.getMediumConfig({ header: this.translate.translate('graphs.addEnvelopeCurve') }),
      result => {
        const addResult = this.envelopeMgr.add(result);
        if (addResult.success) {
          this.announceLiveRegion(this.translate.translate('graphs.envelope.added', { name: result.name }));
        }
      },
    );
  }

  configureEnvelope(envelope: Envelope): void {
    this.openDialog<Envelope>(
      EnvelopeCurvesDialogComponent,
      this.modalHelper.getMediumConfig({
        header: this.translate.translate('graphs.envelope.editTitle', { name: envelope.name }),
        data: { envelope },
      }),
      result => {
        const updateResult = this.envelopeMgr.update(envelope.id, result);
        if (updateResult.success) {
          if (envelope.name !== result.name) {
            this.syncEnvelopeNameInLegend(envelope.id, result.name);
          }
          this.announceLiveRegion(this.translate.translate('graphs.envelope.updated', { name: result.name }));
        }
      },
    );
  }

  private syncEnvelopeNameInLegend(envelopeId: number, newName: string): void {
    const traceId = `env#${envelopeId}`;
    const currentStyle = this.legendMgr.traceStyles().get(traceId);
    if (currentStyle) {
      this.legendMgr.updateStyle(traceId, { ...currentStyle, name: newName });
    }
  }

  async removeEnvelope(envelope: Envelope): Promise<void> {
    const confirmed = await this.confirmationHelper.ask(
      'graphs.envelope.confirmRemove',
      'graphs.envelope.removeTitle',
      { name: envelope.name },
      'pi pi-exclamation-circle',
      'common.buttons.remove',
    );
    if (!confirmed) return;
    this.envelopeMgr.remove(envelope.id);
    this.announceLiveRegion(this.translate.translate('graphs.envelope.removed', { name: envelope.name }));
  }

  onDrop(event: CdkDragDrop<PlotlyDataPoint[]>): void {
    if (!this.legendInteractive() || event.previousIndex === event.currentIndex) {
      return;
    }

    const legendData = this.legendItems();
    const draggedItemName = legendData[event.previousIndex]?.name;

    this.legendMgr.moveTrace(event.previousIndex, event.currentIndex);
    this.plotlyRevision.update(r => r + 1);

    if (draggedItemName) {
      this.legendMgr.setDragFeedback(this.translate.translate('graphs.legend.moved', { name: draggedItemName }));
    }
  }

  onLegendKeyDown(event: KeyboardEvent, index: number): void {
    if (!this.legendInteractive()) return;
    const legendData = this.legendItems();
    const maxIndex = legendData.length - 1;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        if (index > 0 && legendData[index]) {
          this.legendMgr.moveTrace(index, index - 1);
          this.plotlyRevision.update(r => r + 1);
          this.legendMgr.setDragFeedback(this.translate.translate('graphs.legend.movedUp', { name: legendData[index].name }));
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (index < maxIndex && legendData[index]) {
          this.legendMgr.moveTrace(index, index + 1);
          this.plotlyRevision.update(r => r + 1);
          this.legendMgr.setDragFeedback(this.translate.translate('graphs.legend.movedDown', { name: legendData[index].name }));
        }
        break;

      case ' ':
      case 'Enter':
        event.preventDefault();
        this.onToggleTraceVisibility(index);
        break;
    }
  }

  onToggleTraceVisibility(index: number): void {
    if (!this.legendInteractive()) return;
    const isCurrentlyVisible = this.legendMgr.traceVisibility()[index];
    this.applyVisibilityRestyle(index, !isCurrentlyVisible);
    this.legendMgr.toggleVisibility(index);
  }

  private applyVisibilityRestyle(index: number, visible: boolean): void {
    const chart = this.getPlotlyChart();
    if (!chart) return;
    const plotlyIndex = this.plotlyData().length - 1 - index;
    void PlotlyJS.restyle(chart, { visible: visible ? true : 'legendonly' }, [plotlyIndex]);
  }

  onConfigureTrace(index: number): void {
    const legendData = this.legendItems();
    const currentTrace = legendData[index];
    if (!currentTrace) return;
    const measureId = this.legendMgr.getTraceId(currentTrace, index);
    const currentStyles = this.legendMgr.traceStyles().get(measureId);

    const isPolynomial = this.isPolynomialTrace(measureId);
    const defaultMarker = isPolynomial ? DEFAULT_POLYNOMIAL_MARKER : DEFAULT_MEASURE_MARKER;
    const defaultThickness = currentTrace.line?.width ?? (isPolynomial ? DEFAULT_POLYNOMIAL_THICKNESS : 2);

    this.openDialog<CurveStyleResult>(
      CurveStyleDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translate.translate('common.labels.customizeCurve'),
        data: {
          currentStyle: {
            name: currentStyles?.name ?? currentTrace.name ?? '',
            lineType: currentStyles?.lineType ?? this.plotlyDashToLineType(currentTrace.line?.dash),
            color: currentStyles?.color ?? currentTrace.line?.color ?? this.paletteColor(0),
            thickness: currentStyles?.thickness ?? defaultThickness,
            markerSymbol: currentStyles?.markerSymbol ?? currentTrace.marker?.symbol ?? defaultMarker,
          },
        },
      }),
      result => {
        this.legendMgr.updateStyle(measureId, result);
        this.applyRestyleForTrace(index, result);
      },
    );
  }

  private applyRestyleForTrace(index: number, style: Partial<CurveStyleResult>): void {
    const chart = this.getPlotlyChart();
    if (!chart) return;

    const plotlyIndex = this.plotlyData().length - 1 - index;
    const updates: Record<string, unknown> = {};

    if (style.name !== undefined) updates['name'] = style.name;
    if (style.color !== undefined) {
      updates['line.color'] = style.color;
      updates['marker.color'] = style.color;
    }
    if (style.thickness !== undefined) {
      updates['line.width'] = style.thickness;
      updates['marker.size'] = style.thickness * 3;
    }
    if (style.lineType !== undefined) {
      updates['line.dash'] = PLOTLY_LINE_TYPE_MAP[style.lineType];
    }

    const hasLine = style.lineType !== 'none';
    const hasMarker = style.markerSymbol !== 'none';
    if (style.markerSymbol !== undefined || style.lineType !== undefined) {
      if (style.markerSymbol !== undefined) updates['marker.symbol'] = style.markerSymbol;
      updates['mode'] = hasLine && hasMarker ? 'lines+markers' : hasLine ? 'lines' : hasMarker ? 'markers' : 'none';
    }

    if (Object.keys(updates).length > 0) {
      void PlotlyJS.restyle(chart, updates, [plotlyIndex]);
    }
  }

  private applyTraceStyles(
    traces: PlotlyDataPoint[],
    traceStyles: Map<number | string, Partial<CurveStyleResult>>,
  ): PlotlyDataPoint[] {
    return traces.map((trace, index) => {
      const measureId = trace.traceMetadata?.measureId ?? index;
      const customStyle = traceStyles.get(measureId);
      const defaultColor = this.paletteColor(index);
      const traceColor = customStyle?.color ?? trace.line?.color ?? trace.marker?.color ?? defaultColor;
      const lineWidth = customStyle?.thickness ?? trace.line?.width ?? 2;
      const lineDash = customStyle?.lineType ? PLOTLY_LINE_TYPE_MAP[customStyle.lineType] : (trace.line?.dash ?? 'solid');
      const markerSymbol = customStyle?.markerSymbol ?? trace.marker?.symbol;
      const hasLine = (customStyle?.lineType ?? 'solid') !== 'none';
      const hasMarker = markerSymbol !== 'none';
      const traceMode = hasLine && hasMarker ? 'lines+markers' : hasLine ? 'lines' : hasMarker ? 'markers' : 'none';

      return {
        ...trace,
        mode: traceMode,
        line: { ...trace.line, color: traceColor, width: lineWidth, dash: lineDash },
        marker: { ...trace.marker, color: traceColor, size: trace.marker?.size ?? 6, symbol: markerSymbol },
      };
    });
  }

  private applyConvertedTracesForEvolutionTemporelle(
    measures: GraphMeasure[],
    convertedValues: Map<number, CampaignMeasure[]>,
    configs: Map<number, MeasureConfig>,
  ): void {
    const graph = this.currentGraph();
    if (graph?.type !== 1) {
      return;
    }

    const strategy = this.transformStrategies.get(1);
    if (!strategy) {
      return;
    }

    const context: GraphTransformContext = {
      measures: measures,
      values: convertedValues,
      configs: configs,
      customScales: this.customScales(),
      traceOrder: this.legendMgr.traceOrder(),
      traceStyles: this.legendMgr.traceStyles(),
    };

    const evConfig = this.evTempConfig();
    const rawTraces = strategy.transformData(context, evConfig);
    const styledTraces = this.applyTraceStyles(rawTraces, context.traceStyles);
    this.convertedTracesCache.set(styledTraces);
  }

  private announceLiveRegion(message: string): void {
    this.liveRegionMessage.set(message);
    setTimeout(() => this.liveRegionMessage.set(''), this.LIVE_REGION_ANNOUNCEMENT_DURATION);
  }

  // XY : sort_nr est la seule persistance des axes. La sélection d'axes prime
  // (même partielle) ; sinon le repli suit l'ordre persisté, jamais l'ordre de
  // chargement des mesures (arbitraire), qui réécrirait les axes à la volée.
  private xySaveIds(): number[] {
    const assignedIds = [this.measureMgr.selectedMeasureX()?.id, this.measureMgr.selectedMeasureY()?.id].filter(
      (id): id is number => id !== undefined,
    );
    if (assignedIds.length > 0) {
      return assignedIds;
    }
    return [...this.graphicMeasures()].sort((a, b) => a.sortNr - b.sortNr).map(gm => gm.measureId);
  }

  onSave(): void {
    const graph = this.currentGraph();
    if (!graph) {
      return;
    }

    const measureIds = graph.type === 3 ? this.xySaveIds() : this.graphMeasures().map(m => m.id);
    // Fall back to the legend items when the chart isn't plotted so a pre-plot
    // reorder/restyle still persists: order_index is read off the trace metadata,
    // which plotlyData lacks until the chart renders.
    const plotlyData = this.plotlyData();
    const metadataSource = plotlyData.length > 0 ? plotlyData : this.legendItems();

    if (graph.type === 2) {
      graph.metadata = { selectedYears: this.availableYears(), yearType: this.multiYearConfig().yearType };
    } else if (graph.type === 3) {
      this.metadataService.serializeEnvelopeMetadata(metadataSource, this.legendMgr, this.envelopeMgr);
      graph.envelopes = this.envelopeMgr.envelopes();
    }

    this.graphApiService
      .updateGraph(graph)
      .pipe(
        switchMap(() => this.graphApiService.updateGraphMeasures(graph.id, measureIds)),
        switchMap(updatedGraphicMeasures =>
          from(this.metadataService.saveMetadata(updatedGraphicMeasures, this.legendMgr, metadataSource, this.measureConfigs())),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.saveCurrentStateAsBaseline();
          void this.graphStore.loadById(graph.id);
        },
        error: () => {},
      });
  }

  private onEditChart(): void {
    const graph = this.currentGraph();
    if (!graph) return;

    this.openDialog<Graph>(
      CreateGraphDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translate.translate('graphs.dialogs.editGraph', { name: graph.name }),
        data: { mode: 'edit', graph },
      }),
      result => {
        void this.applyEditChart(graph, result);
      },
    );
  }

  private async applyEditChart(graph: Graph, result: Graph): Promise<void> {
    await this.graphStore.update(graph.id, result);
    this.graphStore.invalidateCache();
    void this.graphStore.loadById(graph.id);
  }

  private onDuplicateChart(): void {
    const graph = this.currentGraph();
    const assetId = this.currentAssetId();
    if (!graph || !assetId) return;

    this.openDialog<Partial<Graph>>(
      CreateGraphDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translate.translate('graphs.dialogs.duplicateGraph', { name: graph.name }),
        data: { mode: 'duplicate', graph },
      }),
      result => {
        void this.applyDuplicateChart(graph, assetId, result);
      },
    );
  }

  private async applyDuplicateChart(graph: Graph, assetId: number, result: Partial<Graph>): Promise<void> {
    const createdGraph = await this.graphApiService.duplicateGraphAsync(graph.id, {
      ...(result.name !== undefined && { name: result.name }),
      ...(result.shortName !== undefined && { shortName: result.shortName }),
      ...(result.description !== undefined && { description: result.description }),
    });

    this.graphStore.invalidateCache();
    void this.router.navigate(['/assets', assetId, 'analysis', 'graphs', createdGraph.id]);
  }

  private onDeleteChart(): void {
    const graph = this.currentGraph();
    const assetId = this.currentAssetId();

    if (!graph || !assetId) {
      return;
    }

    this.confirmationHelper.confirmDelete(graph.name, 'graphs.confirmDeleteMessage', () => {
      this.graphStore
        .delete(graph.id)
        .then(() => {
          this.graphStore.invalidateCache();
          void this.router.navigate(['/assets', assetId, 'analysis', 'graphs']);
        })
        .catch(() => undefined);
    });
  }

  private onExportChart(): void {
    const graph = this.currentGraph();
    if (!graph) return;

    this.openDialog<ExportGraphResult>(
      ExportGraphDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translate.translate('graphs.dialogs.exportGraph', { name: graph.name }),
      }),
      result => {
        void this.executeExport(result);
      },
    );
  }

  private async executeExport(options: ExportGraphResult): Promise<void> {
    const chartElement = this.document.querySelector<HTMLElement>('.js-plotly-plot');
    if (!chartElement) {
      this.toastService.error(
        this.translate.translate('common.toasts.graphNotLoaded'),
        false,
        this.translate.translate('common.toasts.exportImpossible'),
      );
      return;
    }

    const graph = this.currentGraph();
    const safeName = graph?.name
      ? graph.name
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .replace(/[^a-zA-Z0-9]/g, '_')
      : 'graph';
    const filename = `cesmon_${safeName}`;
    const originalLayout = structuredClone(this.plotlyLayout());

    await this.exportService.executeExport({
      chartElement,
      filename,
      options,
      ...(graph !== null && { graph }),
      originalLayout,
    });
  }

  async onResetAxes(): Promise<void> {
    const config: Record<string, unknown> = {};
    const graphType = this.currentGraph()?.type;

    if (graphType === 2) {
      this.applyMultiYearXAxisConfig(config);
    } else if (graphType === 3) {
      config['xaxis.autorange'] = true;
      this.applyAllYAxesAutorange(config);
      this.applyCustomScalesConfig(config);
    } else {
      this.applyEvTempXAxisRange(config);
      this.applyAllYAxesAutorange(config);
      this.applyCustomScalesConfig(config);
    }

    await this.applyChartRelayout(config);
  }

  async onAutoscale(): Promise<void> {
    const config: Record<string, unknown> = {};
    const graphType = this.currentGraph()?.type;

    if (graphType === 2) {
      this.applyMultiYearXAxisConfig(config);
    } else if (graphType === 3) {
      config['xaxis.autorange'] = true;
      this.applyAllYAxesAutorange(config);
    } else {
      this.applyEvTempXAxisRange(config);
      this.applyAllYAxesAutorange(config);
    }

    await this.applyChartRelayout(config);
  }

  async onAutocenter(): Promise<void> {
    const chartElement = this.getPlotlyChart();
    if (!chartElement?.data) return;

    const config = this.buildAutocenterConfig(chartElement.data);
    await PlotlyJS.relayout(chartElement, config);
    this.plotlyRevision.update(r => r + 1);
  }

  private getPlotlyChart(): (HTMLElement & { data?: PlotlyDataPoint[] }) | null {
    return this.document.querySelector('.js-plotly-plot');
  }

  private async applyChartRelayout(config: Record<string, unknown>): Promise<void> {
    const chartElement = this.getPlotlyChart();
    if (!chartElement) return;
    await PlotlyJS.relayout(chartElement, config);
    this.plotlyRevision.update(r => r + 1);
  }

  private applyAllYAxesAutorange(config: Record<string, unknown>): void {
    const chartElement = this.getPlotlyChart();
    const layout = (chartElement as { layout?: Record<string, unknown> })?.layout;
    if (!layout) return;

    Object.keys(layout)
      .filter(key => /^yaxis\d*$/.test(key))
      .forEach(axisKey => {
        config[`${axisKey}.autorange`] = true;
      });
  }

  private applyCustomScalesConfig(config: Record<string, unknown>): void {
    const customScales = this.customScales();
    const measures = this.graphMeasures();
    const { measureToAxis } = buildCustomScaleAxisMapping(customScales, measures);

    customScales.forEach((scaleConfig, measureId) => {
      const shortKey = measureToAxis.get(measureId);
      if (!shortKey) return;
      const axisKey = `yaxis${shortKey.slice(1)}`;
      if (scaleConfig.min != null && scaleConfig.max != null) {
        config[`${axisKey}.range`] = [scaleConfig.min, scaleConfig.max];
        config[`${axisKey}.autorange`] = false;
      } else {
        config[`${axisKey}.autorange`] = true;
      }
    });
  }

  // The axis spans the same widened window the data is fetched with, or the end day would be
  // loaded and then clipped off-screen — hiding the most recent measurement of the period.
  private applyEvTempXAxisRange(config: Record<string, unknown>): void {
    const start = this.dateNav.startDate();
    const end = this.dateNav.endDate();
    if (start && end) {
      config['xaxis.range'] = [startOfDay(start).getTime(), endOfDay(end).getTime()];
      config['xaxis.autorange'] = false;
    } else {
      config['xaxis.autorange'] = true;
    }
  }

  private applyMultiYearXAxisConfig(config: Record<string, unknown>): void {
    const years = this.availableYears();
    if (years.length === 0) return;

    // Same bounds the data is fetched with: re-deriving them here once let the axis end at UTC
    // midnight while the fetch ran to the local end of day, clipping the last day off-screen.
    const reference = years.reduce((a, b) => (getYear(a, this.yearType()) <= getYear(b, this.yearType()) ? a : b));
    const { startDate, endDate } = this.getMultiYearRange(reference);

    config['xaxis.range'] = [startDate.getTime(), endDate.getTime()];
    config['xaxis.autorange'] = false;
    config['yaxis.autorange'] = true;
  }

  private buildAutocenterConfig(data: PlotlyDataPoint[]): Record<string, unknown> {
    const config: Record<string, unknown> = {};
    const graphType = this.currentGraph()?.type;
    const amplitudeMap = this.buildAmplitudeMap();
    const configLayout = this.plotlyLayout();

    Object.keys(configLayout).forEach(key => {
      if (!key.match(/^[xy]axis\d*$/)) return;

      const configAxis = configLayout[key] as { range?: [number, number] } | undefined;
      const isX = key.startsWith('x');

      if (graphType === 2 && isX) {
        if (configAxis?.range) {
          config[`${key}.range`] = configAxis.range;
          config[`${key}.autorange`] = false;
        }
        return;
      }

      let amplitude = amplitudeMap.get(key);
      if (amplitude === undefined) {
        if (configAxis?.range?.[0] == null || configAxis.range[1] == null) {
          config[`${key}.autorange`] = true;
          return;
        }
        amplitude = configAxis.range[1] - configAxis.range[0];
      }

      const { min, max } = this.getAxisDataBounds(data, key);
      if (!isFinite(min) || !isFinite(max)) return;

      const center = (min + max) / 2;
      config[`${key}.range`] = [center - amplitude / 2, center + amplitude / 2];
      config[`${key}.autorange`] = false;
    });

    return config;
  }

  private buildAmplitudeMap(): Map<string, number> {
    const amplitudeMap = new Map<string, number>();
    const customScales = this.customScales();
    const measures = this.graphMeasures();
    const { measureToAxis } = buildCustomScaleAxisMapping(customScales, measures);

    customScales.forEach((scaleConfig, measureId) => {
      if (scaleConfig.min != null && scaleConfig.max != null) {
        const shortKey = measureToAxis.get(measureId);
        if (shortKey) {
          const axisKey = `yaxis${shortKey.slice(1)}`;
          amplitudeMap.set(axisKey, scaleConfig.max - scaleConfig.min);
        }
      }
    });
    return amplitudeMap;
  }

  private getAxisDataBounds(data: PlotlyDataPoint[], axisKey: string): { min: number; max: number } {
    const shortKey = axisKey.replace('axis', '');
    const isY = axisKey.startsWith('y');
    const tracesForAxis = data.filter(trace => {
      const traceAxis = isY ? (trace.yaxis ?? 'y') : (trace.xaxis ?? 'x');
      return traceAxis === shortKey;
    });

    let min = Infinity;
    let max = -Infinity;
    tracesForAxis.forEach(trace => {
      const values = (isY ? trace.y : trace.x) as (number | null | undefined)[] | undefined;
      values?.forEach(v => {
        if (typeof v === 'number' && !isNaN(v)) {
          min = Math.min(min, v);
          max = Math.max(max, v);
        }
      });
    });

    return { min, max };
  }

  onAddYear(year: string): void {
    const currentYears = this.availableYears();
    if (currentYears.includes(year)) {
      return;
    }
    const updatedYears = this.measureMgr.sortYears([...currentYears, year]);
    this.availableYears.set(updatedYears);
  }

  onRemoveYear(year: string): void {
    const updatedYears = this.availableYears().filter(y => y !== year);
    this.availableYears.set(updatedYears);
  }

  onYearTypeChange(type: YearType): void {
    this.yearType.set(type);
    setTimeout(() => this.plotlyRevision.update(r => r + 1));
  }

  onRefresh(): void {
    const graph = this.currentGraph();
    if (!graph || !this.canRefreshConditions()) return;

    // Applying the pickers is the user asking for that window, which drops whatever they zoomed to.
    this.zoomedWindow.set(null);

    if (graph.type === 2) {
      this.activeMultiYearConfig.set({
        years: [...this.availableYears()],
        yearType: this.yearType(),
        measureId: this.measureMgr.selectedMeasureMultiYear()?.id ?? null,
      });
    }

    if (graph.assetId !== undefined) {
      this.loadPreconfiguredMeasures(graph.id, graph.assetId, this.getCurrentDateRange(), true);
    }
  }

  onAutoRefreshChange(enabled: boolean): void {
    this.autoRefresh.set(enabled);
    this.viewPrefs.saveAutoRefresh(enabled);
  }

  onToggleLegendPanel(): void {
    this.legendMgr.togglePanel();
    this.viewPrefs.saveLegendCollapsed(this.legendMgr.panelCollapsed());
  }

  async onPointClick(clickEvent: PointClickEvent): Promise<void> {
    const event = clickEvent.event;
    if (!event) return;

    const panelWidth = 320;
    const panelHeight = 200;
    const padding = 16;
    const viewportWidth = this.document.documentElement.clientWidth;
    const viewportHeight = this.document.documentElement.clientHeight;

    let x = event.clientX + padding;
    let y = event.clientY + padding;

    if (x + panelWidth > viewportWidth) {
      x = event.clientX - panelWidth - padding;
    }
    if (y + panelHeight > viewportHeight) {
      y = event.clientY - panelHeight - padding;
    }

    x = Math.max(padding, x);
    y = Math.max(padding, y);

    const point = clickEvent.point;
    await this.resolveCampaignName(point);

    this.pointInfoPosition.set({ x, y });
    this.selectedPoint.set(point);
    this.focusPointInfoPanel();
  }

  closePointInfo(): void {
    this.selectedPoint.set(null);
  }

  private async resolveCampaignName(point: SelectedPointInfo): Promise<void> {
    if (point?.measureType === MeasureType.MANUAL && point.campaignId) {
      const existing = this.campaignStore.getById(point.campaignId);
      if (existing) {
        this.campaignName.set(this.formatCampaignDisplayName(existing));
        return;
      }
      try {
        const campaign = await this.campaignStore.getByIdAsync(point.campaignId);
        this.campaignName.set(campaign ? this.formatCampaignDisplayName(campaign) : undefined);
      } catch {
        this.campaignName.set(undefined);
      }
    } else {
      this.campaignName.set(undefined);
    }
  }

  private formatCampaignDisplayName(campaign: { id: number; timestamp?: string | Date | null }): string {
    return campaign.timestamp ? formatPointDateTime(campaign.timestamp, this.getActiveLang()) : `Campagne ${campaign.id}`;
  }

  formatPointValue(value: string | number | Date | null | undefined): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') return formatNumericValue(value, 4);
    return formatPointDateTime(value, this.getActiveLang());
  }

  private plotlyDashToLineType(dash: string | undefined): LineType {
    return dash && dash in PLOTLY_DASH_TO_LINE_TYPE ? PLOTLY_DASH_TO_LINE_TYPE[dash as PlotlyLineDash] : 'solid';
  }

  private paletteColor(index: number): string {
    const palette = DEFAULT_STYLING.palette;
    return palette.length > 0 ? (palette[index % palette.length] ?? '#000000') : '#000000';
  }

  getMeasureName(measureId: number | string | undefined): string {
    if (!measureId || typeof measureId === 'string') return '';
    const measure = this.graphMeasures().find(m => m.id === measureId);
    return measure?.name ?? '';
  }

  ngOnDestroy(): void {
    this.cancelLoad$.complete();
  }
}
